<template>
  <Transition name="modal-fade">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#070a13]/70 backdrop-blur-xl"
      @click.self="closeModal"
    >
      <div
        ref="cardEl"
        @mousemove="onMouseMove"
        class="group relative w-full max-w-5xl rounded-[28px] border bg-white dark:bg-brand-800 border-slate-100 dark:border-brand-700/50 shadow-[0_30px_120px_-30px_rgba(0,0,0,.3)] dark:shadow-[0_30px_120px_-30px_rgba(0,0,0,.7)] overflow-hidden grid md:grid-cols-2 will-change-transform"
        :style="cardStyle"
      >
        <div
          class="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition duration-300
                 bg-[radial-gradient(1200px_circle_at_var(--x,50%)_var(--y,50%),rgba(0,0,0,.04),transparent_40%)]
                 dark:bg-[radial-gradient(1200px_circle_at_var(--x,50%)_var(--y,50%),rgba(255,255,255,.06),transparent_40%)]"
        ></div>

        <div class="relative hidden md:flex flex-col justify-between p-8 lg:p-12 border-r border-slate-100 dark:border-brand-700/50">
          <div>
            <p class="text-[10px] tracking-[0.3em] text-slate-500 dark:text-slate-400 uppercase">Plataforma Financeira</p>
            <h1 class="mt-3 text-3xl lg:text-4xl font-semibold leading-tight text-slate-900 dark:text-white">
              Organize seu dinheiro com
              <span
                class="bg-gradient-to-r from-brand-500 via-fuchsia-500 to-indigo-500 dark:from-violet-300 dark:via-fuchsia-300 dark:to-indigo-300 bg-clip-text text-transparent"
              >tudo em um só lugar</span>
            </h1>
            <p class="mt-4 text-sm text-slate-600 dark:text-slate-300 max-w-md">
              Gastos, receitas, metas, assinaturas e lembretes automáticos. Controle total, sem fricção.
            </p>
          </div>
          <div class="mt-10 grid grid-cols-3 gap-3">
            <div class="rounded-2xl border p-4 bg-slate-50/70 dark:bg-brand-900/40 border-slate-100 dark:border-brand-700/50">
              <div class="text-base font-semibold text-slate-800 dark:text-slate-100">Criptografia</div>
              <div class="text-xs text-slate-600 dark:text-slate-400">De ponta a ponta</div>
            </div>
            <div class="rounded-2xl border p-4 bg-slate-50/70 dark:bg-brand-900/40 border-slate-100 dark:border-brand-700/50">
              <div class="text-base font-semibold text-slate-800 dark:text-slate-100">Segurança</div>
              <div class="text-xs text-slate-600 dark:text-slate-400">De Dados</div>
            </div>
            <div class="rounded-2xl border p-4 bg-slate-50/70 dark:bg-brand-900/40 border-slate-100 dark:border-brand-700/50">
              <div class="text-base font-semibold text-slate-800 dark:text-slate-100">24/7</div>
              <div class="text-xs text-slate-600 dark:text-slate-400">Suporte</div>
            </div>
          </div>
          <div class="mt-8 flex items-center justify-between">
            <div class="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <span class="material-symbols-outlined text-base">lock</span>
              <span>Autenticação segura • OAuth2 / JWT</span>
            </div>
            <div class="text-xs text-slate-400 dark:text-slate-500">v2.0</div>
          </div>
        </div>

        <div class="relative p-6 sm:p-8 lg:p-12">
          <button
            @click="closeModal"
            aria-label="Fechar modal"
            class="absolute top-4 right-4 size-9 grid place-items-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-brand-700 dark:hover:text-white"
          >
            <span class="material-symbols-outlined">close</span>
          </button>

          <div class="mb-6 sm:mb-8">
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">Entrar</h2>
                <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">Faça login para continuar no ControlEase.</p>
              </div>
            </div>
          </div>

          <transition name="fade">
            <div
              v-if="feedbackMessage.text"
              :class="['mb-6 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium border',
                feedbackMessage.type === 'success'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-400/30'
                  : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-400/30']"
            >
              <span class="material-symbols-outlined text-xl">
                {{ feedbackMessage.type === 'success' ? 'check_circle' : 'error' }}
              </span>
              <span class="truncate">{{ feedbackMessage.text }}</span>
            </div>
          </transition>

          <form @submit.prevent="handleLogin" class="space-y-6" novalidate>
            <div>
              <label for="email" class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">E-mail</label>
              <input
                id="email"
                v-model.trim="email"
                type="email"
                autocomplete="username"
                required
                :disabled="isLoading || isSuccess"
                class="block w-full rounded-2xl border bg-slate-50/70 dark:bg-brand-900/40 border-slate-200 dark:border-brand-700/50 px-4 py-3 text-sm/6
                       text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none transition
                       focus:ring-4 focus:ring-brand-500/20 focus:border-brand-500 dark:focus:border-brand-500"
                placeholder="voce@exemplo.com"
              />
            </div>

            <div>
              <div class="flex items-center justify-between mb-2">
                <label for="password" class="block text-xs font-medium text-slate-600 dark:text-slate-400">Senha</label>
                <a
                  href="#"
                  @click.prevent="emit('switch-view', 'forgotPassword')"
                  class="hidden sm:inline text-xs text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
                >
                  Esqueceu?
                </a>
              </div>
              <div class="relative">
                <input
                  id="password"
                  :type="showPassword ? 'text' : 'password'"
                  v-model.trim="password"
                  autocomplete="current-password"
                  minlength="6"
                  required
                  :disabled="isLoading || isSuccess"
                  @keyup="checkCaps"
                  class="block w-full rounded-2xl border bg-slate-50/70 dark:bg-brand-900/40 border-slate-200 dark:border-brand-700/50 px-4 py-3 text-sm/6
                         text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none transition pr-12
                         focus:ring-4 focus:ring-brand-500/20 focus:border-brand-500 dark:focus:border-brand-500"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  @click="togglePassword"
                  aria-label="Mostrar/ocultar senha"
                  class="absolute inset-y-0 right-0 px-3 grid place-items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  <span class="material-symbols-outlined">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
                </button>
              </div>
              <div v-if="capsOn" class="mt-2 inline-flex items-center gap-1.5 text-[12px] text-amber-600 dark:text-amber-300/90">
                <span class="material-symbols-outlined text-[16px]">keyboard_capslock</span>
                Caps Lock ativo
              </div>
            </div>

            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm">
              <label class="inline-flex items-center gap-2 select-none">
                <input
                  type="checkbox"
                  v-model="remember"
                  class="appearance-none size-4 rounded border border-slate-300 dark:border-brand-600 bg-slate-100 dark:bg-brand-900/40 
                         checked:bg-brand-600 dark:checked:bg-brand-500 transition grid place-items-center"
                />
                <span class="text-slate-700 dark:text-slate-300">Lembrar de mim</span>
              </label>
              <a
                href="#"
                @click.prevent="emit('switch-view', 'forgotPassword')"
                class="sm:hidden text-xs text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
              >
                Esqueceu sua senha?
              </a>
            </div>

            <button
              type="submit"
              :disabled="isLoading || isSuccess"
              class="relative inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-2xl
                     bg-gradient-to-r from-brand-600 via-fuchsia-600 to-indigo-600 px-5 py-3.5 text-sm font-semibold text-white
                     shadow-[0_12px_40px_-8px_rgba(124,58,237,.55)] transition active:scale-[.99] disabled:opacity-70 group"
            >
              <span
                class="absolute inset-0 opacity-0 group-hover:opacity-100 transition [background:radial-gradient(900px_circle_at_var(--x,50%)_var(--y,50%),rgba(255,255,255,.18),transparent_45%)]"
              ></span>
              <span v-if="isLoading" class="loader-modern mr-1.5"></span>
              <span v-if="isSuccess" class="material-symbols-outlined -ml-1">check</span>
              <span class="relative z-10">
                {{ isLoading ? 'Verificando…' : (isSuccess ? 'Sucesso!' : 'Entrar no ControlEase') }}
              </span>
              <span class="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/10"></span>
            </button>

            <p class="sm:hidden text-center text-sm text-slate-600 dark:text-slate-300">
              Não tem uma conta?
              <a
                href="#"
                @click.prevent="emit('switch-view', 'register')"
                class="font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
              >
                Criar agora
              </a>
            </p>
          </form>

          <div class="mt-6 flex items-center gap-3">
            <div class="h-px flex-1 bg-slate-200 dark:bg-brand-700/50"></div>
            <span class="text-xs text-slate-500 dark:text-slate-400">ou</span>
            <div class="h-px flex-1 bg-slate-200 dark:bg-brand-700/50"></div>
          </div>

          <div class="mt-6 flex flex-col items-center gap-6">
            <div id="googleSignInButtonContainer" class="w-full flex justify-center"></div>
            <p class="hidden sm:block text-sm text-slate-600 dark:text-slate-300">
              Não tem uma conta?
              <a href="#" @click.prevent="emit('switch-view', 'register')" class="font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300">
                Criar agora
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>


<script setup>
import { ref, onUnmounted, computed, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['close', 'switch-view', 'login-success'])

const closeModal = () => {
  emit('close')
}

const authStore = useAuthStore()

const email = ref('')
const password = ref('')
const remember = ref(true)
const showPassword = ref(false)
const isLoading = ref(false)
const isSuccess = ref(false)
const feedbackMessage = ref({ text: '', type: '' })
const capsOn = ref(false)
const googleButtonObserver = ref(null)

const googleClientId = '725822552292-at7cd8i0e0bo49d5g3amisj73l942u10.apps.googleusercontent.com'

const togglePassword = () => (showPassword.value = !showPassword.value)
const checkCaps = (e) => { capsOn.value = e.getModifierState && e.getModifierState('CapsLock') }

const handleLogin = async () => {
  if (!email.value || !password.value) return
  isLoading.value = true
  feedbackMessage.value = { text: '', type: '' }
  try {
    const result = await authStore.login({ email: email.value, password: password.value, remember: remember.value })
    if (result?.success) {
      isSuccess.value = true
      feedbackMessage.value = { text: 'Login bem-sucedido!', type: 'success' }
      setTimeout(() => {
        emit('login-success', { role: result.role })
        closeModal()
        resetForm()
      }, 900)
    } else {
      feedbackMessage.value = { text: result?.message || 'Falha no login. Verifique suas credenciais.', type: 'error' }
    }
  } catch (err) {
    feedbackMessage.value = { text: 'Erro ao conectar ao servidor. Tente novamente.', type: 'error' }
  } finally {
    isLoading.value = false
  }
}

const resetForm = () => {
  email.value = ''
  password.value = ''
  isLoading.value = false
  isSuccess.value = false
  feedbackMessage.value = { text: '', type: '' }
}

const setupGoogleButton = () => {
  const container = document.getElementById('googleSignInButtonContainer')
  if (!container) return

  const renderGoogleButton = () => {
    if (!window.google?.accounts?.id) return
    container.innerHTML = ''
    const isDark = document.documentElement.classList.contains('dark')
    window.google.accounts.id.renderButton(container, {
      theme: isDark ? 'filled_black' : 'outline',
      size: 'large',
      shape: 'pill',
      text: 'continue_with',
      logo_alignment: 'left',
    })
  }
  
  const init = () => {
    if (!window.google?.accounts?.id) return
    window.google.accounts.id.initialize({ client_id: googleClientId, callback: window.handleCredentialResponse, ux_mode: 'popup' })
    renderGoogleButton()
    
    const observer = new MutationObserver((mutationsList) => {
      for(const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          renderGoogleButton()
        }
      }
    });
    observer.observe(document.documentElement, { attributes: true });
    googleButtonObserver.value = observer
  }

  if (!window.google?.accounts?.id) {
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = init
    document.head.appendChild(script)
  } else {
    init()
  }
}

window.handleCredentialResponse = async (response) => {
  isLoading.value = true
  feedbackMessage.value = { text: '', type: '' }
  try {
    const result = await authStore.loginWithGoogle({ token: response.credential })
    if (result?.success) {
      isSuccess.value = true
      feedbackMessage.value = { text: 'Login bem-sucedido!', type: 'success' }
      setTimeout(() => {
        emit('login-success', { role: result.role })
        closeModal()
        resetForm()
      }, 900)
    } else if (result?.requiresSetup) {
      emit('switch-view', 'completeSignup', { email: result.email, nome: result.nome })
      resetForm()
    } else {
      feedbackMessage.value = { text: result?.message || 'Falha na autenticação com Google.', type: 'error' }
    }
  } catch (err) {
    feedbackMessage.value = { text: 'Não foi possível autenticar com o Google.', type: 'error' }
  } finally {
    isLoading.value = false
  }
}

const cardEl = ref(null)
const mouseX = ref(0)
const mouseY = ref(0)
const onMouseMove = (e) => {
  const el = cardEl.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  const x = e.clientX - rect.left
  const y = e.clientY - rect.top
  mouseX.value = x
  mouseY.value = y
  el.style.setProperty('--x', `${x}px`)
  el.style.setProperty('--y', `${y}px`)
}
const cardStyle = computed(() => ({
  transform: 'translateZ(0)',
}))

watch(() => props.show, (newVal) => {
  if (newVal) {
    resetForm()
    setTimeout(setupGoogleButton, 50)
  }
})

onUnmounted(() => {
  if (window.google?.accounts?.id?.cancel) window.google.accounts.id.cancel()
  if (googleButtonObserver.value) {
    googleButtonObserver.value.disconnect()
  }
})
</script>

<style>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity .25s ease
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0
}
.loader-modern {
  width: 18px;
  height: 18px;
  border: 2px solid transparent;
  border-top-color: #fff;
  border-right-color: #fff;
  border-radius: 50%;
  display: inline-block;
  animation: spin .7s linear infinite
}
@keyframes spin {
  to {
    transform: rotate(360deg)
  }
}
</style>